import { Component, h, State } from '@stencil/core';
import { collectionGroup, getDocs, orderBy, query, where } from '@firebase/firestore';
import { httpsCallable } from '@firebase/functions';
import spacetime from 'spacetime';

import { FirebaseService, orderConverter } from '../../utils/firebase';
import { formatOrderDate } from '../../utils/utils';
import { settings } from '../../store/settings.store';
import { customer } from '../../store/customer.store';
import { closeAccountFlow } from '../../modals/account.modal';

import { Order } from '@artask/tambr-models/models/orders.model';
import { WidgetDeleteOrderData, WidgetOrderPaymentMethodUpdateRequest } from '@artask/tambr-models/models/widget';
import { PlatformCustomerPaymentMethod } from '@artask/tambr-models/models/customer.model';



@Component({
  tag: 'tambr-account-activity',
  styleUrl: 'tambr-account-activity.css',
  shadow: true,
})
export class TambrAccountActivity {

  @State() loading: boolean = true;
  @State() loaded: boolean = false;
  @State() error: string | null = null;

  @State() previousOrders: Order[] = [];
  @State() upcomingOrders: Order[] = [];

  @State() paymentMethodLoading: boolean = true;

  confirmDialogEl: HTMLTambrConfirmationDialogElement;
  messageEl: HTMLTambrMessageElement;

  paymentMethodSelectEl: HTMLTambrPaymentMethodSelectElement;
  paymentMethodAddEl: HTMLTambrPaymentMethodAddElement;

  componentDidLoad() { 
    this.loadCustomerOrders();
  }

  disconnectedCallback() {
    this.closeConfirmationDialog(); 
  }

  close() { closeAccountFlow(); }

  async loadCustomerOrders(){
    this.loading = true;
    try {

      const ordersCollectionQuery = query(
        collectionGroup( FirebaseService.firestore, 'orders' ),
        where( 'customer.email', '==', customer.platformCustomer.email ),
        orderBy( 'startTime', 'desc' )
      ).withConverter( orderConverter );
      const ordersCollection = await getDocs( ordersCollectionQuery );

      const orders = ordersCollection.docs.map( doc => doc.data() );

      this.upcomingOrders = orders
        .filter( (order: Order) => this.isUpcoming( order) )
        .sort( (a, b) => a.startTime.seconds - b.startTime.seconds );

      this.previousOrders = orders
        .filter( (order: Order) => !this.isUpcoming( order ) )
        .sort( (a, b) => b.endTime.seconds - a.endTime.seconds ); 

    } catch(e) {
      console.error(e);
      this.error = e.message;
    }
    this.loaded = true;
    this.loading = false;
  }


  isUpcoming( order: Order ){
    const tz = settings.businessSettings.timezone;
    return spacetime( order.endTime.toMillis(), tz ).isAfter( spacetime.now( tz ) );
  }


  confirmCancelCheckout( orderId: string ){
    if ( document.getElementsByTagName('tambr-confirmation-dialog').length ) return;
    
    this.confirmDialogEl = document.createElement('tambr-confirmation-dialog');
    this.confirmDialogEl.message = `Are you sure you would like to cancel this order?`;
    this.confirmDialogEl.confirmText = 'Cancel this order';
    this.confirmDialogEl.cancelText = 'Dismiss';

    this.confirmDialogEl.addEventListener( 'tambrConfirmationDialogResponse', async (response: any) => {
      if ( response.detail === 'confirm' ) {
        this.confirmDialogEl.loading = true;
        try {
          
          const deleteOrder = httpsCallable<WidgetDeleteOrderData>( FirebaseService.functions, 'widget_deleteOrder' );
          const deleteOrderData = { businessId: settings.businessId, orderId }
          const deleteOrderResult = await deleteOrder( deleteOrderData );
          this.loadCustomerOrders();
          console.log( 'deleteOrderResult', deleteOrderResult );

        } catch(e) {
          console.error(e);
          this.showErrorMessage( e.message );
        }
      }
      this.closeConfirmationDialog(); 
    });
    
    document.body.appendChild( this.confirmDialogEl );  
  }

  closeConfirmationDialog() {
    if ( document.getElementsByTagName('tambr-confirmation-dialog').length ) document.body.removeChild( this.confirmDialogEl ); 
  }

  openOrderPaymentMethodSelectDialog( order: Order ) {
    console.log( 'order', order)
    if ( document.getElementsByTagName('tambr-payment-method-select').length ) return;
    this.paymentMethodSelectEl = document.createElement('tambr-payment-method-select');
    this.paymentMethodSelectEl.userId = customer.platformCustomer.uid;
    this.paymentMethodSelectEl.initialPaymentMethodId = order.paymentMethodId;
    this.paymentMethodSelectEl.addEventListener( 'tambrSelectPaymentMethod', 
      ( event: CustomEvent<PlatformCustomerPaymentMethod> ) => this.selectPaymentMethod( event.detail, order.id ) 
    );
    this.paymentMethodSelectEl.addEventListener( 'tambrSelectPaymentMethodDialogClose', () => this.paymentMethodSelectEl.remove() );
    this.paymentMethodSelectEl.addEventListener( 'tambrAddNewPaymentMethod', () => {
      this.paymentMethodSelectEl.remove();
      this.openPaymentMethodAddDialog( order.id );
    });
    document.body.appendChild(this.paymentMethodSelectEl);
  }

  openPaymentMethodAddDialog( orderId: string ) {
    if ( document.getElementsByTagName('tambr-payment-method-add').length ) return;    
    this.paymentMethodAddEl = document.createElement('tambr-payment-method-add');
    this.paymentMethodAddEl.addEventListener( 'tambrPaymentMethodAddClose', () => this.closePaymentMethodAddDialog() );
    this.paymentMethodAddEl.addEventListener( 'tambrPaymentMethodAddSuccess', 
      ( event: CustomEvent<string> ) => this.handlePaymentMethodAddSuccess( event.detail, orderId ) 
    );
    document.body.appendChild( this.paymentMethodAddEl );
  }

  
  async selectPaymentMethod( paymentMethod: PlatformCustomerPaymentMethod, orderId: string ) {
    this.paymentMethodSelectEl.actionLoading = true;
    await this.updatePaymentMethod( paymentMethod.id, orderId )
    if ( document.getElementsByTagName('tambr-payment-method-select').length ) this.paymentMethodSelectEl.remove();
  }
  
  async handlePaymentMethodAddSuccess( paymentMethodId: string, orderId: string ) {
    this.paymentMethodAddEl.actionLoading = true;
    await this.updatePaymentMethod( paymentMethodId, orderId );
    this.closePaymentMethodAddDialog();
  }

  async updatePaymentMethod( paymentMethodId: string, orderId: string ) {
    try {
      const updatePaymentMethod = 
        httpsCallable<WidgetOrderPaymentMethodUpdateRequest>( FirebaseService.functions, 'widget_orderPaymentMethodUpdate' );
      const updatePaymentMethodData: WidgetOrderPaymentMethodUpdateRequest = { businessId: settings.businessId, orderId, paymentMethodId };
      const updatePaymentMethodResult = await updatePaymentMethod( updatePaymentMethodData );
      this.loadCustomerOrders();
      console.log(updatePaymentMethodResult);
    } catch(e) {
      console.error(e);
      this.showErrorMessage( e.message );
    }
  }

  closePaymentMethodAddDialog() {
    if ( document.getElementsByTagName('tambr-payment-method-add').length ) this.paymentMethodAddEl.remove();
  }

  showErrorMessage(message: string) {
    this.messageEl = document.createElement('tambr-message');
    this.messageEl.header = 'Error';
    this.messageEl.message = message;
    this.messageEl.addEventListener( 'tambrMessageClose', () => this.messageEl.remove() );
    document.body.appendChild( this.messageEl );
  }


  render() {
    return ([      
      <div class={'loading-overlay' + (this.loading ? ' open' : '') }>
        <tambr-loading-spinner />
      </div>,

      ( this.loaded
        ?            
          ( this.previousOrders.length || this.upcomingOrders.length ) 
            ? [ 
                this.renderUpcomingOrders(),
                this.renderPreviousOrders()
              ]
            : <div class='no-orders'>
                <h3>Welcome to { settings.businessSettings.businessName }</h3>
                <div class='tambr-action-buttons'>
                  <button type='submit' class={ 'tambr-button' } onClick={ () => this.close() }>
                    <span>Book a session</span>
                  </button>
                </div>
              </div>             
        : '' 
      )
    ]);
  }

  renderUpcomingOrders() {
    if ( this.upcomingOrders.length ) {
      return (
        <div class='activity-type'>
          <div class='activity-type-title'>Upcoming Orders</div>
          { this.upcomingOrders.map( order => this.renderOrder(order) ) }
        </div>
      );
    }
  }

  renderPreviousOrders() {
    if ( this.previousOrders.length ) {
      return (
        <div class='activity-type'>
          <div class='activity-type-title'>Previous Orders</div>
          { this.previousOrders.map( order => this.renderOrder(order) ) }
        </div>
      );
    }
  }

  renderOrder( order: Order ) {
    return (
      <div class='activity-order'>
        <div class='activity-order-left-col'>
          <div class='order-header'>
            <span class='order-id'>Order #{ order.id }</span> 
            { this.renderLabel( order ) }
          </div>
          <div><span class='order-business-title'>{ order.business.title }</span>, { order.space?.title }</div>         
          <div>
            <span>{ formatOrderDate( order.date ) }, </span>
            <span class='order-time'>{ spacetime( order.startTime.toMillis(), settings.businessSettings.timezone ).format('time') } - </span> 
            <span class='order-time'>{ spacetime( order.endTime.toMillis(), settings.businessSettings.timezone ).format('time') }</span> 
          </div>
          { this.renderPaymentMethod( order ) }
        </div>
        <div class='activity-order-right-col'>
          { this.renderOrderPaymentMethodUpdateButton( order ) }
          { this.renderCancelButton( order ) }
        </div>
      </div>
    )
  }

  renderLabel( order: Order ) {
    const isCancelled = order.status === 'cancelled';
    const isPaymentFailed = order.paymentStatus === 'scheduled_payment_failed';
    return ([
      isCancelled && (<span class='order-label'>Cancelled</span>),
      isPaymentFailed && (<span class='order-label warning'>Payment failed</span>),
    ]);
  }

  renderPaymentMethod( order: Order ) {
    if ( !order.paymentMethodId ) return ( <div class='order-payment'>Payment method: cash</div> );
    if ( !order.paymentMethodDetails ) return;
    return (
      <div class='order-payment'>
        Payment method: <span class='order-payment-method'>{ order.paymentMethodDetails.brand } ••• ••• ••• { order.paymentMethodDetails.last4 }</span> 
      </div>
    );    
  }

  renderOrderPaymentMethodUpdateButton( order: Order ) {
    const isConfirmed = ['active', 'confirmed'].includes( order.status );
    const isScheduledPayment = ['scheduled', 'scheduled_payment_cancelled', 'scheduled_payment_failed'].includes( order.paymentStatus );
    if ( isConfirmed && isScheduledPayment ) {
      return <a class='update-button' onClick={ () => this.openOrderPaymentMethodSelectDialog( order ) }>Update payment method</a>
    }    
  }

  renderCancelButton( order: Order ) {
    const orderStartTime = spacetime( order.startTime.toMillis(), settings.businessSettings.timezone );
    const now = spacetime.now( settings.businessSettings.timezone );
    if ( orderStartTime.isBefore( now ) || ![ 'active', 'confirmed' ].includes( order.status ) ) return null;

    const cancelSettings = settings.businessSettings.scheduleSettings.cancellationLimit;
    if ( !cancelSettings || !cancelSettings.enabled ) return null;

    const cancelLimitAmount = cancelSettings.amount;
    const cancelLimitUnit = cancelSettings.unit;

    if ( now.diff( orderStartTime, cancelLimitUnit ) >= cancelLimitAmount ) {
      return (<a class='cancel-button' onClick={ () => this.confirmCancelCheckout(order.id) }>Cancel order</a>);
    }      
    return null;
  }

}
